import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Layout, Card, Typography, Tabs, Row, Col, Statistic, Tag, 
  Space, Avatar, Spin, Alert, List
} from 'antd';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import {
  CalendarOutlined, 
  ClockCircleOutlined, 
  UserOutlined, 
  BookOutlined, 
  TrophyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  BarChartOutlined,
  PieChartOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import { useTranslation } from 'react-i18next';
import './MyPerformance.css';

const { Content } = Layout;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

const StudentPerformance = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [registrations, setRegistrations] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL;

useEffect(() => {
  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');

      const [registrationsRes, attendanceRes] = await Promise.all([
        axios.get(`${API_URL}/registrations/student/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/attendance/student/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const registrations = registrationsRes.data.registrations;
      const attendance = attendanceRes.data;

      setRegistrations(registrations);
      setAttendance(attendance);
    } catch (error) {
      console.error('❌ Error fetching student data:', error);
      setError(error.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, [id]);




  const getAttendanceForClass = (classId) => {
    return attendance.find((log) => log.classid === classId);
  };

  // Calculate performance statistics using real data
  const calculateStats = () => {
    if (!registrations.length) {
      return {
        totalClasses: 0,
        presentClasses: 0,
        lateClasses: 0,
        absentClasses: 0,
        attendanceRate: 0,
        punctualityRate: 0,
        completionRate: 0
      };
    }

    const allClasses = registrations.flatMap(reg => reg.schedule || []);
    const totalClasses = allClasses.length;
    
    // Get attendance status for each class
    const classesWithAttendance = allClasses.map(cls => {
      const attendanceLog = getAttendanceForClass(cls.classid);
      return {
        ...cls,
        status: attendanceLog ? attendanceLog.status : 'No Attendance'
      };
    });

    const presentClasses = classesWithAttendance.filter(cls => cls.status === 'Present').length;
    const lateClasses = classesWithAttendance.filter(cls => cls.status === 'Late').length;
    const absentClasses = classesWithAttendance.filter(cls => cls.status === 'Absent').length;
    
    const attendanceRate = totalClasses > 0 ? Math.round(((presentClasses + lateClasses) / totalClasses) * 100) : 0;
    const punctualityRate = (presentClasses + lateClasses) > 0 ? Math.round((presentClasses / (presentClasses + lateClasses)) * 100) : 0;
    const completionRate = totalClasses > 0 ? Math.round((presentClasses / totalClasses) * 100) : 0;
    
    return {
      totalClasses,
      presentClasses,
      lateClasses,
      absentClasses,
      attendanceRate,
      punctualityRate,
      completionRate
    };
  };

  const stats = calculateStats();

  // Generate weekly progress data from actual attendance
  const generateWeeklyProgress = () => {
    if (!attendance.length) return [];
    
    // Group attendance by week
    const weeklyData = {};
    attendance.forEach(att => {
      const date = new Date(att.timestamp || att.date);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      const weekKey = weekStart.toISOString().split('T')[0];
      
      if (!weeklyData[weekKey]) {
        weeklyData[weekKey] = { total: 0, present: 0, late: 0 };
      }
      
      weeklyData[weekKey].total++;
      if (att.status === 'Present') weeklyData[weekKey].present++;
      if (att.status === 'Late') weeklyData[weekKey].late++;
    });

    // Convert to chart format
    return Object.entries(weeklyData)
      .sort(([a], [b]) => new Date(a) - new Date(b))
      .slice(-4) // Last 4 weeks
      .map(([week, data], index) => ({
        week: `Week ${index + 1}`,
        attendance: Math.round(((data.present + data.late) / data.total) * 100),
        performance: Math.round((data.present / data.total) * 100)
      }));
  };

  const weeklyProgress = generateWeeklyProgress();

  // Generate subject performance data from registrations
  const subjectPerformance = registrations.map(reg => {
    const classStats = calculateClassStats(reg);
    return {
      subject: reg.subjects && reg.subjects.length > 0 ? reg.subjects[0].title : reg.class_code,
      attendance: classStats.attendanceRate,
      completion: classStats.completionRate,
      hours: parseFloat(reg.total_hours) || 0
    };
  });

  function calculateClassStats(registration) {
    if (!registration.schedule || !registration.schedule.length) {
      return { attendanceRate: 0, completionRate: 0 };
    }

    const schedule = registration.schedule;
    const totalClasses = schedule.length;
    
    const classesWithAttendance = schedule.map(cls => {
      const attendanceLog = getAttendanceForClass(cls.classid);
      return attendanceLog ? attendanceLog.status : 'No Attendance';
    });

    const presentClasses = classesWithAttendance.filter(status => status === 'Present').length;
    const lateClasses = classesWithAttendance.filter(status => status === 'Late').length;
    
    return {
      attendanceRate: Math.round(((presentClasses + lateClasses) / totalClasses) * 100),
      completionRate: Math.round((presentClasses / totalClasses) * 100)
    };
  }

  // Data for charts
  const attendanceData = [
    { name: 'Present', value: stats.presentClasses, color: '#52c41a' },
    { name: 'Late', value: stats.lateClasses, color: '#faad14' },
    { name: 'Absent', value: stats.absentClasses, color: '#ff4d4f' }
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return new Intl.DateTimeFormat(i18n.language, options).format(date).toUpperCase();
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    return `${hours}:${minutes}`;
  };

  const calculateEndTime = (startTime, duration) => {
    if (!startTime || !duration) return '';
    
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    
    // Handle duration in HH:MM format
    let durationHours = 0;
    let durationMinutes = 0;
    
    if (duration.includes(':')) {
      [durationHours, durationMinutes] = duration.split(':').map(Number);
    } else {
      // If duration is just a number, treat as hours
      durationHours = parseFloat(duration);
      durationMinutes = (durationHours % 1) * 60;
      durationHours = Math.floor(durationHours);
    }

    let endHours = startHours + durationHours;
    let endMinutes = startMinutes + durationMinutes;
    
    if (endMinutes >= 60) {
      endHours += Math.floor(endMinutes / 60);
      endMinutes %= 60;
    }
    
    return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
  };

  const getStatusConfig = (status) => {
    const configs = {
      'Present': {
        color: 'success',
        text: t('finished') || 'Completed',
        icon: CheckCircleOutlined
      },
      'Late': {
        color: 'warning',
        text: t('late') || 'Late', 
        icon: ExclamationCircleOutlined
      },
      'Absent': {
        color: 'error',
        text: t('absent') || 'Absent',
        icon: CloseCircleOutlined
      },
      'No Attendance': {
        color: 'default',
        text: t('scheduled') || 'Scheduled',
        icon: ClockCircleOutlined
      }
    };
    return configs[status] || configs['No Attendance'];
  };


  // Loading and error states
  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Sidebar />
        <Layout>
          <Header />
          <Content style={{ padding: '50px', textAlign: 'center' }}>
            <Spin size="large" />
            <div style={{ marginTop: 20 }}>
              <Text>{t('Loading your performance data...')}</Text>
            </div>
          </Content>
        </Layout>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Sidebar />
        <Layout>
          <Header />
          <Content style={{ padding: '50px' }}>
            <Alert
              message="Error Loading Data"
              description={error}
              type="error"
              showIcon
              style={{ maxWidth: 600, margin: '0 auto' }}
            />
          </Content>
        </Layout>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar />
      <Layout>
        <Header />
        <Content  className="performance-content">

          {/* Main Tabs */}
          <Card>
            <Tabs activeKey={activeTab} onChange={setActiveTab} size="large">
              
              {/* Overview Tab */}
              <TabPane 
                tab={
                  <span>
                    {t('Overview') || 'Overview'}
                  </span>
                } 
                key="overview"
              >
                <Row gutter={[24, 24]}>
                  {/* Key Statistics */}
                  <Col span={24}>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12} md={6}>
                        <Card>
                          <Statistic
                            title={t('Overall Attendance') || 'Overall Attendance'}
                            value={stats.attendanceRate}
                            suffix="%"
                            prefix={<CalendarOutlined style={{ color: '#1890ff' }} />}
                            valueStyle={{ color: stats.attendanceRate >= 80 ? '#3f8600' : '#cf1322' }}
                          />
                          <Text type="secondary">
                            {stats.presentClasses + stats.lateClasses}/{stats.totalClasses} classes
                          </Text>
                          <br />
                        </Card>
                      </Col>

                      <Col xs={24} sm={12} md={6}>
                        <Card>
                          <Statistic
                            title={t('Punctuality Rate') || 'Punctuality Rate'}
                            value={stats.punctualityRate}
                            suffix="%"
                            prefix={<ClockCircleOutlined style={{ color: '#52c41a' }} />}
                            valueStyle={{ color: stats.punctualityRate >= 90 ? '#3f8600' : '#faad14' }}
                          />
                          <Text type="secondary">
                            {stats.presentClasses} on-time arrivals
                          </Text>
                          <br />
                        </Card>
                      </Col>

                      <Col xs={24} sm={12} md={6}>
                        <Card>
                          <Statistic
                            title={t('Total Classes') || 'Total Classes'}
                            value={stats.totalClasses}
                            prefix={<BookOutlined style={{ color: '#722ed1' }} />}
                            valueStyle={{ color: '#722ed1' }}
                          />
                          <Text type="secondary">
                            {registrations.length} active courses
                          </Text>
                        </Card>
                      </Col>

                      <Col xs={24} sm={12} md={6}>
                        <Card>
                          <Statistic
                            title={t('Completion Rate') || 'Completion Rate'}
                            value={stats.completionRate}
                            suffix="%"
                            prefix={<TrophyOutlined style={{ color: '#fa8c16' }} />}
                            valueStyle={{ color: stats.completionRate >= 80 ? '#3f8600' : '#cf1322' }}
                          />
                          <Text type="secondary">
                            Classes completed successfully
                          </Text>
                          <br />
                        </Card>
                      </Col>
                    </Row>
                  </Col>

                  {/* Charts Row */}
                  <Col xs={24} lg={12}>
                    <Card 
                      title={
                        <Space>
                          <PieChartOutlined />
                          {t('Attendance Breakdown') || 'Attendance Breakdown'}
                        </Space>
                      }
                      style={{ height: 400 }}
                    >
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie
                            data={attendanceData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {attendanceData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </Card>
                  </Col>

                  <Col xs={24} lg={12}>
                    <Card 
                      title={
                        <Space>
                          <LineChartOutlined />
                          {t('Weekly Progress') || 'Weekly Progress'}
                        </Space>
                      }
                      style={{ height: 400 }}
                    >
                      <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={weeklyProgress}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="week" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line 
                            type="monotone" 
                            dataKey="attendance" 
                            stroke="#1890ff" 
                            strokeWidth={3}
                            dot={{ fill: '#1890ff' }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="performance" 
                            stroke="#52c41a" 
                            strokeWidth={3}
                            dot={{ fill: '#52c41a' }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Card>
                  </Col>

                  {/* Subject Performance */}
                  <Col span={24}>
                    <Card 
                      title={
                        <Space>
                          <BarChartOutlined />
                          {t('Subject Performance') || 'Subject Performance'}
                        </Space>
                      }
                    >
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={subjectPerformance}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="subject" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="attendance" fill="#1890ff" name="Attendance %" />
                          <Bar dataKey="completion" fill="#52c41a" name="Completion %" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card>
                  </Col>
                </Row>
              </TabPane>

              {/* Subjects Tab */}
              <TabPane 
                tab={
                  <span>
                    {t('Subjects') || 'Subjects'}
                  </span>
                } 
                key="subjects"
              >
                <Row gutter={[24, 24]}>
                  {registrations.map((registration) => {
                    const classStats = calculateClassStats(registration);
                    
                    return (
                      <Col span={24} key={registration.groupid}>
                        <Card 
                          title={
                            <Space>
                              <Avatar style={{ backgroundColor: '#1890ff' }}>
                                {registration.class_code.charAt(0)}
                              </Avatar>
                              <div>
                                <Title level={4} style={{ margin: 0 }}>
                                  {registration.class_code}
                                </Title>
                                <Text type="secondary">
                                  {registration.subjects?.[0]?.title || 'No Subject'}
                                </Text>
                              </div>
                            </Space>
                          }
                          extra={
                            <div style={{ textAlign: 'right' }}>
                              <Text strong style={{ fontSize: 24, color: '#1890ff' }}>
                                {classStats.attendanceRate}%
                              </Text>
                              <br />
                              <Text type="secondary">Attendance Rate</Text>
                            </div>
                          }
                        >
                          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                            <Col xs={24} md={8}>
                              <Card size="small" style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
                                <Statistic
                                  title="Present"
                                  value={registration.schedule?.filter(cls => {
                                    const attendanceLog = getAttendanceForClass(cls.classid);
                                    return attendanceLog?.status === 'Present';
                                  }).length || 0}
                                  prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                                  valueStyle={{ color: '#52c41a' }}
                                />
                              </Card>
                            </Col>
                            
                            <Col xs={24} md={8}>
                              <Card size="small" style={{ backgroundColor: '#fffbe6', border: '1px solid #ffe58f' }}>
                                <Statistic
                                  title="Late"
                                  value={registration.schedule?.filter(cls => {
                                    const attendanceLog = getAttendanceForClass(cls.classid);
                                    return attendanceLog?.status === 'Late';
                                  }).length || 0}
                                  prefix={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
                                  valueStyle={{ color: '#faad14' }}
                                />
                              </Card>
                            </Col>
                            
                            <Col xs={24} md={8}>
                              <Card size="small" style={{ backgroundColor: '#fff2f0', border: '1px solid #ffccc7' }}>
                                <Statistic
                                  title="Absent"
                                  value={registration.schedule?.filter(cls => {
                                    const attendanceLog = getAttendanceForClass(cls.classid);
                                    return attendanceLog?.status === 'Absent';
                                  }).length || 0}
                                  prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                                  valueStyle={{ color: '#ff4d4f' }}
                                />
                              </Card>
                            </Col>
                          </Row>

                          <Row gutter={[16, 16]}>
                            {registration.schedule
                              ?.sort((a, b) => new Date(a.date) - new Date(b.date))
                              .map((cls, index) => {
                                const attendanceLog = getAttendanceForClass(cls.classid);
                                const status = attendanceLog ? attendanceLog.status : 'No Attendance';
                                const statusConfig = getStatusConfig(status);
                                
                                return (
                                  <Col xs={24} sm={12} md={8} key={index}>
                                    <Card 
                                      size="small"
                                      style={{ 
                                        borderLeft: `4px solid ${
                                          status === 'Present' ? '#52c41a' : 
                                          status === 'Late' ? '#faad14' : 
                                          status === 'Absent' ? '#ff4d4f' : '#d9d9d9'
                                        }`
                                      }}
                                    >
                                      <div style={{ marginBottom: 8 }}>
                                        <Text strong>{cls.subject}</Text>
                                        <Tag 
                                          color={statusConfig.color} 
                                          style={{ float: 'right' }}
                                          icon={<statusConfig.icon />}
                                        >
                                          {statusConfig.text}
                                        </Tag>
                                      </div>
                                      
                                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                        <Space>
                                          <CalendarOutlined />
                                          <Text type="secondary">{formatDate(cls.date)}</Text>
                                        </Space>
                                        <Space>
                                          <ClockCircleOutlined />
                                          <Text type="secondary">
                                            {formatTime(cls.time)} - {calculateEndTime(cls.time, cls.hours)}
                                          </Text>
                                        </Space>
                                        <Space>
                                          <UserOutlined />
                                          <Text type="secondary">{cls.teacher || 'No Teacher'}</Text>
                                        </Space>
                                        {cls.classroom && (
                                          <Space>
                                            <BookOutlined />
                                            <Text type="secondary">Room {cls.classroom}</Text>
                                          </Space>
                                        )}
                                        {attendanceLog?.homework && (
                                          <Tag 
                                            color={attendanceLog.homework === 'YES' ? 'green' : 'red'}
                                            style={{ marginTop: 4 }}
                                          >
                                            HW: {attendanceLog.homework}
                                          </Tag>
                                        )}
                                        {attendanceLog?.comment && (
                                          <Alert
                                            message={attendanceLog.comment}
                                            type={
                                              status === 'Present' ? 'success' :
                                              status === 'Late' ? 'warning' :
                                              status === 'Absent' ? 'error' : 'info'
                                            }
                                            showIcon
                                            style={{ marginTop: 8 }}
                                          />
                                        )}
                                      </Space>
                                    </Card>
                                  </Col>
                                );
                              }) || []}
                          </Row>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              </TabPane>

              {/* Timeline Tab */}
              <TabPane 
                tab={
                  <span>
                    {t('Timeline') || 'Timeline'}
                  </span>
                } 
                key="timeline"
              >
                <Card title={t('Activity Timeline') || 'Activity Timeline'}>
                  <List
                    itemLayout="horizontal"
                    dataSource={registrations
                      .flatMap(reg => 
                        (reg.schedule || []).map(cls => ({
                          ...cls,
                          class_code: reg.class_code,
                          attendanceLog: getAttendanceForClass(cls.classid)
                        }))
                      )
                      .sort((a, b) => new Date(a.date) - new Date(b.date))}
                    renderItem={(cls) => {
                      const status = cls.attendanceLog ? cls.attendanceLog.status : 'No Attendance';
                      const statusConfig = getStatusConfig(status);
                      
                      return (
                        <List.Item>
                          <List.Item.Meta
                            avatar={
                              <Avatar 
                                style={{ 
                                  backgroundColor: 
                                    status === 'Present' ? '#52c41a' : 
                                    status === 'Late' ? '#faad14' : 
                                    status === 'Absent' ? '#ff4d4f' : '#d9d9d9'
                                }}
                                icon={<statusConfig.icon />}
                              />
                            }
                            title={
                              <Space>
                                <Text strong>{cls.class_code} - {cls.subject}</Text>
                                <Tag color={statusConfig.color}>{statusConfig.text}</Tag>
                              </Space>
                            }
                            description={
                              <Space direction="vertical" size="small">
                                <Text type="secondary">
                                  {formatDate(cls.date)} • {formatTime(cls.time)} - {calculateEndTime(cls.time, cls.hours)}
                                </Text>
                                <Text type="secondary">Teacher: {cls.teacher || 'No Teacher'}</Text>
                                {cls.classroom && (
                                  <Text type="secondary">Room: {cls.classroom}</Text>
                                )}
                                {cls.attendanceLog?.homework && (
                                  <Tag color={cls.attendanceLog.homework === 'YES' ? 'green' : 'red'}>
                                    Homework: {cls.attendanceLog.homework}
                                  </Tag>
                                )}
                                {cls.attendanceLog?.comment && (
                                  <Text italic type="secondary">"{cls.attendanceLog.comment}"</Text>
                                )}
                              </Space>
                            }
                          />
                        </List.Item>
                      );
                    }}
                  />
                </Card>
              </TabPane>
            </Tabs>
          </Card>
        </Content>
      </Layout>
    </Layout>
  );
};

export default withRoleAccess(StudentPerformance, ['admin', 'superadmin']);
